import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { callLcosService } from "../data/lcosServices";
import { useCanFetchStaffPositionHistory } from "./use-staff/use-can-fetch-staff-member-access";
import { useAccessToken } from "./use-access-token";

export type UseEmploymentHistoryOptions = {
  enabled?: boolean;
};

export interface EmployeeHistory {
  id: number;
  position: string;
  jobType: string;
  start: string;
  end: string;
  separation_reason?: string;
}

export interface HourlyRateHistoryRow {
  id: number;
  hourlyRate: string | number;
  start: string;
  end: string;
}

export interface EmploymentHistoryResponse {
  employeeHistory: EmployeeHistory[];
  hourlyRateHistory: HourlyRateHistoryRow[];
}

export const useEmploymentHistory = (
  centerId: string | undefined,
  staffMemberId: string | undefined,
  options?: UseEmploymentHistoryOptions
) => {
  const queryClient = useQueryClient();
  const { getToken } = useAccessToken();
  const hasRequiredIds = Boolean(centerId && staffMemberId);
  const canFetchByDefault = useCanFetchStaffPositionHistory(staffMemberId);
  const fetchAllowed =
    hasRequiredIds && (options?.enabled !== undefined ? options.enabled : canFetchByDefault);

  const queryKey = ["employmentHistoryData", centerId, staffMemberId];

  const getEmploymentHistoryData = async () => {
    return getEmploymentHistory(await getToken(), centerId, staffMemberId);
  };

  const employmentHistoryQuery = useQuery({
    queryKey,
    queryFn: getEmploymentHistoryData,
    enabled: fetchAllowed,
    retry: false
  });

  const deleteMutator = useMutation({
    mutationFn: async (employmentHistoryId: number) =>
      deleteEmploymentHistory(await getToken(), centerId, staffMemberId, employmentHistoryId),

    onSuccess: updatedData => {
      queryClient.invalidateQueries({ queryKey: ["centerStaffData", centerId] });
      return queryClient.setQueryData(queryKey, () => ({
        employeeHistory: [...updatedData.employeeHistory],
        hourlyRateHistory: [...(updatedData.hourlyRateHistory ?? [])]
      }));
    }
  });

  const latest = useMemo(() => {
    if (employmentHistoryQuery.data?.employeeHistory?.length) {
      return employmentHistoryQuery.data.employeeHistory[employmentHistoryQuery.data.employeeHistory.length - 1];
    } else {
      return undefined;
    }
  }, [employmentHistoryQuery.data]);

  return {
    employmentHistory: employmentHistoryQuery.data?.employeeHistory ?? [],
    hourlyRateHistory: employmentHistoryQuery.data?.hourlyRateHistory ?? [],
    isLoading: fetchAllowed && employmentHistoryQuery.isLoading,
    deleteMutator,
    latest
  };
};

export const getEmploymentHistory = (
  accessToken: string,
  centerId: string | undefined,
  staffMemberId: string | undefined
) => {
  return callLcosService<EmploymentHistoryResponse>(accessToken, `/api/position/${centerId}/${staffMemberId}`);
};

export const deleteEmploymentHistory = (
  accessToken: string,
  centerId: string | undefined,
  staffMemberId: string | undefined,
  employmentHistoryId: number
) => {
  return callLcosService<EmploymentHistoryResponse>(
    accessToken,
    `/api/staff/${centerId}/${staffMemberId}/employment_history/${employmentHistoryId}`,
    "DELETE"
  );
};
